import { useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { getOccUserIfm, checkReviewPermission } from '@/services/apis/aiwriter'

interface IUserIfm {
  name?: string
  userId?: string
  email?: string
  avatar?: string
}

enum ISystemType {
  IWENCAI = 'iwencai',
  AINVEST = 'ainvest',
}

const host = window.location.host
const loginUrl = 'https://www.ainvest.com/login/'
const iwencaiHost = 'iwencai.com'
const loginCookieKeys = ['u_name', 'userid']

const checkSystemType = () => {
  if (host.lastIndexOf(iwencaiHost) !== -1) {
    return ISystemType.IWENCAI
  }
  return ISystemType.AINVEST
}

export const goLogin = () => {
  window.location.href = `${loginUrl}?redirectUrl=${window.location.href}&flow=login`
}

const getCookieDomains = () => {
  const hostname = window.location.hostname
  const segments = hostname.split('.').filter(Boolean)
  const domains = new Set<string | undefined>([undefined, hostname])

  if (segments.length >= 2) {
    domains.add(`.${segments.slice(-2).join('.')}`)
  }

  if (segments.length >= 3) {
    domains.add(`.${segments.slice(-3).join('.')}`)
  }

  return Array.from(domains)
}

const clearLoginCookies = () => {
  const domains = getCookieDomains()

  loginCookieKeys.forEach((cookieKey) => {
    domains.forEach((domain) => {
      Cookies.remove(cookieKey, { path: '/', domain })
    })
    Cookies.remove(cookieKey)
  })
}

const useAccountModel = () => {
  const [open, setOpen] = useState<boolean>(false)
  const [userIfm, setUserIfm] = useState<IUserIfm>({} as IUserIfm)
  const [loginStatus, setLoginStatus] = useState<boolean>(true)
  const [checkStatus, setCheckStatus] = useState<boolean>(false)
  
  // 初始化输出系统类型
  const systemType = checkSystemType()

  const logout = async () => {
    clearLoginCookies()
    setUserIfm({} as IUserIfm)
    setLoginStatus(false)
    setCheckStatus(false)
    setOpen(false)
    goLogin()
  }

  const login = () => {
    if (systemType === ISystemType.IWENCAI) {
      setOpen(true)
      return
    }

    // 前往登录
    goLogin()
    return
  }

  const getUserIfm = () => {
    let name
    let userId
    name = Cookies.get('u_name')
    userId = Cookies.get('userid')

    setUserIfm({
      name,
      userId
    })

    // 设置校验状态为true
    setCheckStatus(true)

    // 异步获取用户详情（邮箱等）
    fetchUserDetail()

    return name
  }

  const fetchUserDetail = async () => {
    try {
      const result = await getOccUserIfm()
      if (result?.email || result?.avatar) {
        setUserIfm(prev => ({
          ...prev,
          email: result.email,
          avatar: result.avatar,
        }))
      }
    } catch (error) {
      // 静默失败，不影响主流程
      console.error('Failed to fetch user detail:', error)
    }
  }

  // 判断是不是游客
  const isVisitor = (name?: string) => {
    // 如果用户名不存在，则认为是游客
    if (!name) {
      return true
    }

    // 如果是mt_开头的，则认为是游客
    if (name.startsWith('mt_')) {
      return true
    }
    return false
  }

  const checkLoginStatus = (name?: string) => {
    // 如果已经登录
    if (!isVisitor(name)) {
      setLoginStatus(true)
      setOpen(false)
      return
    }
    setLoginStatus(false)

    // 如果wencai的地址开弹窗
    if (systemType === ISystemType.IWENCAI) {
      // 并开始弹窗
      setOpen(true)
    } else {
      goLogin()
    }
  }

  const initAccount = () => {
    const name = getUserIfm()
    checkLoginStatus(name)
  }

  const getPermission = async () => {
    const permission = await checkReviewPermission()
    return permission
  }


  useEffect(() => {
    if (checkStatus) {
      checkLoginStatus(userIfm?.name)
    }
  }, [userIfm, checkStatus])

  useEffect(() => {
    initAccount()
  }, [])

  return {
    systemType,
    open,
    setOpen,
    userIfm,
    getUserIfm,
    loginStatus,
    logout,
    login,
    getPermission,
  }
}

export default useAccountModel
